import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    completePlanDeveloppementStepRequest,
    createDemandeFormationFromPlanRequest,
    fetchPlanDeveloppementStepRequest,
    generatePlanDeveloppementRecommendationsRequest,
} from "../../../../redux/actions/performanceActions";
import DemandeFormModal from "../../../Formation/ResponsableFormation/RessourcesJs/demandes/DemandeFormModal";
import PlanDeveloppementCard from "../../components/PlanDeveloppementCard";
import {
    buildFormationCardFromItem,
    normalizeFormationForRequest,
    staticPlanCards,
} from "../../components/planDeveloppementCardUtils";
import { asArray, StepState } from "../performanceUi";
import "./PlanDeveloppementStep.css";

export default function PlanDeveloppementStep({ progressId }) {
    const dispatch = useDispatch();
    const slice = useSelector((state) => state.performance.planDeveloppement);
    const data = slice.stepByProgressId[String(progressId)];
    const loading = slice.loadingByProgressId[String(progressId)];
    const error = slice.errorByProgressId[String(progressId)];
    const recommendations = asArray(data?.recommendations);
    const items = asArray(data?.items);
    const hasFormationCampaign = data?.formationCampaignAssociated !== false;

    const [selectedPlanFormation, setSelectedPlanFormation] = useState(null);
    const [openDemandeModal, setOpenDemandeModal] = useState(false);
    const [isSubmittingDemande, setIsSubmittingDemande] = useState(false);

    useEffect(() => {
        if (progressId) {
            dispatch(fetchPlanDeveloppementStepRequest(progressId));
        }
    }, [dispatch, progressId]);

    const planFormationCards = useMemo(() => items.map(buildFormationCardFromItem), [items]);
    const cardsToDisplay = hasFormationCampaign ? planFormationCards : staticPlanCards;
    const displayedCards = useMemo(() => cardsToDisplay.slice(0, 3), [cardsToDisplay]);

    const handleCloseDemandeFormation = () => {
        setOpenDemandeModal(false);
        setSelectedPlanFormation(null);
        setIsSubmittingDemande(false);
    };

    useEffect(() => {
        if (!isSubmittingDemande || loading) {
            return;
        }

        const submittedItemId = selectedPlanFormation?.itemId;
        const submittedItem = submittedItemId
            ? items.find((item) => String(item.id) === String(submittedItemId))
            : null;

        if (error) {
            setIsSubmittingDemande(false);
            return;
        }

        if (submittedItemId && submittedItem?.demandeId) {
            handleCloseDemandeFormation();
        }
    }, [error, isSubmittingDemande, items, loading, selectedPlanFormation]);

    const handleOpenDemandeFormation = (card) => {
        const normalizedFormation = normalizeFormationForRequest({
            ...card,
            title: card.nom || card.titre,
            category: card.category,
            duration: card.duration,
        });

        setSelectedPlanFormation({
            ...card,
            formation: normalizedFormation,
        });
        setOpenDemandeModal(true);
    };

    const handleSubmitDemandeFormation = (payloadFromModal) => {
        if (!selectedPlanFormation?.itemId) {
            return;
        }

        setIsSubmittingDemande(true);
        dispatch(
            createDemandeFormationFromPlanRequest({
                evaluationProgressId: progressId,
                planDeveloppementItemId: selectedPlanFormation.itemId,
                mode: payloadFromModal.mode,
                period: payloadFromModal.period,
                niveau: payloadFromModal.niveau,
                certifiante: payloadFromModal.certifiante,
                priorite: payloadFromModal.priorite,
                objectifs: payloadFromModal.objectifs,
                objectifs2: payloadFromModal.objectifs2,
                objectifs3: payloadFromModal.objectifs3,
                description: payloadFromModal.description,
            })
        );
    };

    return (
        <StepState loading={loading} error={error} empty={!data}>
            <div className="plan-container">
                {!hasFormationCampaign ? (
                    <>
                        <h3 className="plan-section">Formations suggerees</h3>
                        <div className="plan-manager-grid">
                            {displayedCards.map((card) => (
                                <PlanDeveloppementCard
                                    key={card.id}
                                    item={card}
                                    onDemander={handleOpenDemandeFormation}
                                    variant="manager"
                                />
                            ))}
                        </div>
                    </>
                ) : (
                    <>
                        {data?.blockedReason && <div className="perf-state perf-state--warning">{data.blockedReason}</div>}
                        <div className="perf-actions perf-actions--top">
                            <button className="perf-btn" type="button" onClick={() => dispatch(generatePlanDeveloppementRecommendationsRequest(progressId))}>
                                Generer recommandations
                            </button>
                            <button className="perf-btn perf-btn--primary" type="button" onClick={() => dispatch(completePlanDeveloppementStepRequest(progressId))}>
                                Terminer l'etape
                            </button>
                        </div>

                        <h3 className="plan-section">Recommande pour vous</h3>
                        {recommendations.length === 0 && <div className="empty-state">Aucune recommandation.</div>}
                        <div className="plan-grid">
                            {recommendations.map((recommendation, index) => (
                                <div className="plan-card" key={recommendation.competenceNom || recommendation.skillNom || index}>
                                    <div className="plan-card__content">
                                        <h4>{recommendation.competenceNom || recommendation.skillNom || "Recommendation"}</h4>
                                        <span className="status IN_PROGRESS">Gap {recommendation.gap ?? "-"}</span>
                                        <p>{recommendation.reason || "Recommandation proposee par le backend."}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <h3 className="plan-section">Formations de mon plan</h3>
                        {displayedCards.length === 0 && <div className="empty-state">Aucun element selectionne</div>}
                        <div className="plan-manager-grid">
                            {displayedCards.map((card) => (
                                <PlanDeveloppementCard
                                    key={card.id}
                                    item={card}
                                    onDemander={handleOpenDemandeFormation}
                                    variant="manager"
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>

            <DemandeFormModal
                open={openDemandeModal}
                onClose={handleCloseDemandeFormation}
                onSubmit={handleSubmitDemandeFormation}
                formation={selectedPlanFormation?.formation || null}
                campagneId={null}
                type="FORMATION"
                loading={isSubmittingDemande}
                requireCampagneSelection={false}
            />
        </StepState>
    );
}
